// frontend/src/components/TableList/TableList.tsx

import React, { useState, useEffect } from 'react';
import { tableService } from '../../services/table.service';
import { useAuth } from '../../contexts/AuthContext';
import './TableList.css';

type ITableInfo = {
  id: number;
  table_name: string;
  columns: string[];
  filename?: string;
  total_rows?: number;
  status?: string;
  created_at?: string;
  trace_id?: string | null;
};

const renderValue = (value: any): React.ReactNode => {
  if (value === null || value === undefined) return '—';
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const TableList: React.FC = () => {
  const [tables, setTables] = useState<ITableInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const { selectTrace, selectedTraceId, selectedDataset } = useAuth();

  useEffect(() => {
    loadTables();
  }, []);

  const loadTables = async () => {
    try {
      setLoading(true);
      const data = await tableService.getTablesAndColumns();
      setTables(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao carregar tabelas');
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (table: ITableInfo) => {
    if (!table.trace_id) return;
    selectTrace(table.trace_id, table.table_name);
  };

  if (loading) return <div className="loading">Carregando...</div>;
  if (error) return <div className="error">{error}</div>;

  return (
    <div className="table-list-container">
      <h2>📊 Lista de Tabelas</h2>

      {selectedTraceId && (
        <div className="selected-info">
          ✅ Selecionado: <strong>{selectedDataset}</strong>
          <span className="selected-info-trace">🔗 {selectedTraceId}</span>
        </div>
      )}

      {tables.length === 0 ? (
        <p className="empty">Nenhuma tabela encontrada</p>
      ) : (
        <div className="table-grid">
          {tables.map((table) => {
            const isSelected = selectedTraceId === table.trace_id;

            return (
              <div
                key={table.trace_id}
                className={`table-card ${isSelected ? 'selected' : ''}`}
                onClick={() => handleSelect(table)}
                style={{ cursor: table.trace_id ? 'pointer' : 'default' }}
              >
                {isSelected && (
                  <span className="selected-badge">📌 SELECIONADO</span>
                )}

                <h3>{renderValue(table.table_name)}</h3>

                {table.trace_id && (
                  <span className="trace-id">🔗 {table.trace_id}</span>
                )}

                {table.filename && (
                  <span className="filename">{renderValue(table.filename)}</span>
                )}

                <div className="card-meta">
                  <span className="badge">{table.columns?.length ?? 0} colunas</span>
                  {table.status && (
                    <span className="status-badge">{renderValue(table.status)}</span>
                  )}
                </div>

                <div className="columns-preview">
                  {renderValue(table.columns)}
                </div>

                <div className="card-footer">
                  {table.total_rows !== undefined && table.total_rows !== null && (
                    <span className="rows">📊 {renderValue(table.total_rows)} linhas</span>
                  )}
                  {table.created_at && (
                    <span className="created-at">{renderValue(table.created_at)}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TableList;